import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import FormNav from 'common/components/FormNav';
import { useAuthLicenseAddOns } from 'common/features/licenseFeatures/useAuthLicenseAddOns';
import { useSettingValue } from 'common/hooks/useSettingValue';
import { generateRandomId } from 'common/utils/utils';
import { useFormFields } from 'libs/hooksLib';
import { isEqual } from 'lodash';
import * as moment from 'moment';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEventActorsAction, getActors, resetActors } from 'redux/slices/actorSlice';
import { fetchEventAssetsAction, getAssets, getAssetsPending, resetAssets } from 'redux/slices/assetSlice';
import { getUser } from 'redux/slices/authSlice';
import {
    cancelCloseCustomDialogAction,
    closeCustomDialogAction,
    getIsDialogOnClose,
} from 'redux/slices/customDialogSlice';
import { getDiscounts, resetDiscounts, setDiscounts } from 'redux/slices/discountSlice';
import { setExpandAccordionEdited } from 'redux/slices/eventSlice';
import { getEventStickers, resetEventStickers, setEventStickers } from 'redux/slices/eventStickerSlice';
import { fetchOrganizationProviderAction, getProvider } from 'redux/slices/organizationSlice';
import { resetProducts } from 'redux/slices/productSlice';
import { getSocial, resetSocial } from 'redux/slices/socialSlice';
import { getStreamConnectors, resetStreamConnectors } from 'redux/slices/streamConnectorsSlice';
import { getCmsFingerPrint } from 'redux/slices/userSlice';

import { Stack } from '@mui/material';
import useFlexBoxStyles from 'common/flexBox';
import LayoutForm from 'common/layouts/LayoutForm';
import { cancelCloseNestedDialogAction, closeNestedDialogAction } from 'redux/slices/nestedDialogSlice';
import { getOrgTvChannels, getTvChannels } from 'redux/slices/tvChannelSlice';
import EventFormActors from './EventFormActors';
import EventFormAssets from './EventFormAssets';
import EventFormConfirm from './EventFormConfirm';
import EventFormSettings from './EventFormSettings';

function filterReminders(reminders) {
    return reminders?.filter((reminder) => Object.keys(reminder).length !== 0) || [];
}

const EventForm = (props) => {
    const { action, event, onSubmit, wizardMode, onCancel } = props;

    const { t } = useTranslation();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const dispatch = useDispatch();
    const assets = useSelector(getAssets);
    const assetsPending = useSelector(getAssetsPending);
    const actors = useSelector(getActors);
    const discounts = useSelector(getDiscounts);
    const stickers = useSelector(getEventStickers);
    const user = useSelector(getUser);
    const provider = useSelector(getProvider);
    const cmsFingerPrint = useSelector(getCmsFingerPrint);
    const isDrawerOnClose = useSelector(getIsDialogOnClose);
    const { enqueueSnackbar } = useSnackbar();
    const [step, setStep] = useState(1);
    const [subForm, setSubForm] = useState(null);
    const [initialAssets, setInitialAssets] = useState([]);
    const [initialDiscounts, setInitialDiscounts] = useState([]);
    const [initialStickers, setInitialStickers] = useState([]);
    const [initialActors, setInitialActors] = useState([]);
    const [eventActors, setEventActors] = useState([]);
    const isAuthorize = useAuthLicenseAddOns();
    const enableDiscount = useSettingValue(SettingsKey.ENABLE_DISCOUNT_CODES);
    const enableGift = useSettingValue(SettingsKey.ENABLE_SHOPIFY_GIFT_PRODUCTS);
    const orgTvChannels = useSelector(getOrgTvChannels);
    const enableTvWidget = useSettingValue(SettingsKey.ENABLE_TV_WIDGET) || orgTvChannels?.length > 0;
    const enableLabels = useSettingValue(SettingsKey.ENABLE_LABELS);
    const channelSubscriptionEnable = useSettingValue(SettingsKey.ENABLE_WIDGET_CHANNEL_SUBSCRIPTION);
    const enableTvChannelManager = useSettingValue(SettingsKey.ENABLE_TV_CHANNEL_MANAGER);
    const deleteConfirmTitle = t('event.eventForm.deleteConfirm');
    const deleteConfirmMessage = t('event.eventForm.deleteMessage');

    const connectors = useSelector(getStreamConnectors);
    const social = useSelector(getSocial);
    const tvChannels = useSelector(getTvChannels);

    const availableChannels = useMemo(() => {
        const validOrgTvChannels = orgTvChannels && Array.isArray(orgTvChannels);
        const validTvChannels = tvChannels && Array.isArray(tvChannels);
        let activatedChannels = [];

        if (validTvChannels && validOrgTvChannels) {
            tvChannels.forEach((channel) => {
                if (!!orgTvChannels.find(({ id }) => id === channel.id)) {
                    activatedChannels.push(channel);
                }
            });
        }
        return activatedChannels;
    }, [orgTvChannels, tvChannels]);

    const initialForm = useMemo(
        () => ({
            name: event.name,
            adminUserId: event.adminUserId,
            chatManagerUsersIds: event?.chatManagerUsersIds || [],
            isAdminHost: event.type === 'one_to_one',
            releaseDate: event.releaseDate ? moment(event.releaseDate).set('second', 0).set('millisecond', 0) : '',
            reminders: event.reminders,
            recurrency: event.recurrency ? event.recurrency : '',
            description: event.description,
            inviteeName: event.inviteeName,
            inviteeEmail: event.inviteeEmail,
            calendlyEventUri: event.calendlyEventUri,
            calendlyEventTypeUri: event.calendlyEventTypeUri,
            duration: event.duration,
            type: event.type,
            labels: event.labels,
            instant: event.instant,
            sessionRequestId: event.sessionRequestId,
            isPublic: event.isPublic || false,
            channelsIds: event.channelsIds || null,
            hostProvider: event.hostProvider || 'web',
            mainImage: event.mainImage || null,
            record: false,
            limitConcurrentUsers: event.limitConcurrentUsers,
            callToActions: event.callToActions || [],
            notifySubscribers: event.notifySubscribers || false,
            skipLeadCreation: event.skipLeadCreation || false,
        }),
        [event],
    );

    const [fields, handleFieldChange, resetForm] = useFormFields(initialForm);

    useEffect(() => {
        if (!provider) {
            dispatch(fetchOrganizationProviderAction());
        }
    }, [dispatch, provider]);

    useEffect(() => {
        if (event.id || event.eventId) {
            dispatch(fetchEventActorsAction(event.id));
            if (event.discountCodes) {
                dispatch(setDiscounts(event.discountCodes));
                setInitialDiscounts(event.discountCodes);
            }
            if (event.stickers) {
                dispatch(setEventStickers(event.stickers));
                setInitialStickers(event.stickers);
            }
        }

        if (event.id || event.eventId) {
            dispatch(
                fetchEventAssetsAction({
                    eventId: event.id || event.eventId,
                    onSuccess: (_assets) => {
                        setInitialAssets(_assets);
                    },
                }),
            );
        }

        return () => {
            dispatch(resetActors());
            dispatch(resetAssets());
            dispatch(resetDiscounts());
            dispatch(resetEventStickers());
            dispatch(resetStreamConnectors());
        };
    }, [event, dispatch]);

    useEffect(() => {
        return () => dispatch(resetStreamConnectors());
    }, [dispatch]);

    useEffect(() => {
        dispatch(resetProducts([]));
        return () => dispatch(resetProducts([]));
    }, [dispatch]);

    useEffect(() => {
        return () => dispatch(resetSocial());
    }, [dispatch]);

    useEffect(() => {
        const host = actors.find((actor) => actor.role === 'host');
        let _actors = [
            ...(actors || []),
            ...(event.type === 'one_to_one' && (!actors || !host)
                ? [
                      {
                          userId: user?.userId,
                          email: user?.email,
                          isAdmin: true,
                          role: 'host',
                          avatar: user?.avatar,
                      },
                  ]
                : []),
        ];
        if (host?.userId === event.adminUserId) {
            handleFieldChange({
                target: {
                    name: 'isAdminHost',
                    value: true,
                },
            });
        }
        setEventActors(_actors);
        setInitialActors(_actors);
    }, [actors, event.adminUserId, event.type, handleFieldChange, user?.avatar, user?.email, user?.userId]);

    useEffect(() => {
        if (!enableDiscount) {
            dispatch(resetDiscounts());
        }
    }, [dispatch, enableDiscount]);

    const userDidChange = useCallback(() => {
        const { isAdminHost: isAdminHost1, ...original } = initialForm;
        const { isAdminHost: isAdminHost2, ...current } = fields;
        return (
            !isEqual(original, current) ||
            !isEqual(
                sort(
                    initialActors.map((actor) => mapActor(actor)),
                    'email',
                ),
                sort(
                    eventActors.map((actor) => mapActor(actor)),
                    'email',
                ),
            ) ||
            !isEqual(
                sort(
                    initialAssets.map((asset) => mapAsset(asset)),
                    'id',
                ),
                sort(
                    assets.map((asset) => mapAsset(asset)),
                    'id',
                ),
            ) ||
            !isEqual(initialDiscounts, discounts) ||
            !isEqual(initialStickers, stickers)
        );
    }, [
        assets,
        discounts,
        eventActors,
        fields,
        initialActors,
        initialAssets,
        initialDiscounts,
        initialForm,
        initialStickers,
        stickers,
    ]);

    const restart = useCallback(() => {
        setStep(1);
        resetForm(initialForm);
    }, [initialForm, resetForm]);

    const finalCancel = useCallback(() => {
        restart();
        try {
            onCancel?.();
        } catch (e) {
            console.error(`Error invoking event form onCancel callback: ${e.message}`);
        }
        dispatch(closeCustomDialogAction());
        dispatch(closeNestedDialogAction());
    }, [dispatch, restart, onCancel]);

    const manageCancel = useCallback(() => {
        if (!userDidChange()) {
            finalCancel();
        } else {
            setOpenCancelDlg(true);
        }
    }, [finalCancel, userDidChange]);

    useEffect(() => {
        if (isDrawerOnClose) {
            manageCancel();
        }
    }, [isDrawerOnClose, manageCancel]);

    const [openCancelDlg, setOpenCancelDlg] = useState(false);

    const isMultiGuest = useMemo(() => isAuthorize(['multi_guest']), [isAuthorize]);

    const getGuestLimit = useCallback(
        () => (event.type === 'one_to_one' ? 30 : isMultiGuest && fields.hostProvider === 'web' ? 7 : 0),
        [event.type, fields.hostProvider, isMultiGuest],
    );

    const [guestLimit, setGuestLimit] = useState(getGuestLimit());

    useEffect(() => {
        setGuestLimit(getGuestLimit());
        setEventActors((eventActors) =>
            eventActors.filter(
                (actor) =>
                    actor.role !== 'guest' ||
                    eventActors
                        .filter((a) => a.role === 'guest')
                        .slice(0, getGuestLimit())
                        .find((guest) => guest.email === actor.email),
            ),
        );
    }, [getGuestLimit]);

    useEffect(() => {
        if (fields.record) {
            setEventActors((eventActors) => eventActors.filter((actor) => actor.role !== 'guest'));
        }
    }, [fields.record]);

    useEffect(() => {
        if (fields.isPublic && enableTvChannelManager && availableChannels.length === 1) {
            let channelsIds = [];
            channelsIds = [availableChannels[0].id];
            handleFieldChange({
                target: {
                    name: 'channelsIds',
                    value: channelsIds,
                },
            });
        }
    }, [fields.isPublic, enableTvChannelManager, availableChannels, handleFieldChange]);

    const openSubForm = (form) => {
        setSubForm(form);
    };

    const closeSubForm = () => {
        setSubForm(null);
    };

    const mapActor = (actor) => {
        return {
            avatar: actor?.avatar || '',
            email: actor?.email || '',
            isAdmin: actor?.isAdmin || false,
            role: actor?.role || '',
            userId: actor?.userId || '',
            chatManagerUserId: actor?.chatManagerUserId || '',
        };
    };

    const mapAsset = (asset) => {
        const { id } = asset;
        return {
            id,
        };
    };

    const sort = (array, field) => {
        return array.sort((a, b) => a[field].localeCompare(b[field]));
    };

    const cancelDlg = (event) => {
        setOpenCancelDlg(false);
        dispatch(cancelCloseCustomDialogAction());
        dispatch(cancelCloseNestedDialogAction());
    };

    const cancel = (event) => {
        event.preventDefault();
        manageCancel();
    };

    function filterAssets(assets) {
        let filteredAssets = [];
        assets.forEach((asset) => {
            //Only unique, i am not sure if this is still needed
            if (filteredAssets.indexOf(asset.id) === -1) {
                if (asset.type !== 'product') {
                    filteredAssets.push(asset.id);
                }
            }
        });
        return filteredAssets.filter((a) => a);
    }

    function handleSubmit(e) {
        dispatch(setExpandAccordionEdited(''));
        e.preventDefault();

        let channelsIdsValue = fields.channelsIds?.length === 0 ? null : fields.channelsIds;

        if (!fields.isPublic) {
            if (enableTvChannelManager) {
                channelsIdsValue = [];
            } else {
                channelsIdsValue = null;
            }
        }

        onSubmit(
            {
                name: fields.name,
                adminUserId: fields.adminUserId,
                instant: fields.instant,
                cmsFingerPrint,
                chatManagerUsersIds: fields?.chatManagerUsersIds,
                ...(fields.instant ? { releaseDate: new Date() } : { releaseDate: new Date(fields.releaseDate) }),
                reminders: filterReminders(fields.reminders),
                description: fields.description,
                recurrency: fields.recurrency,
                actors: eventActors
                    .filter((actor) =>
                        !fields.record ? actor.role === 'host' || actor.role === 'guest' : actor.role === 'host',
                    )
                    .map((actor) => ({
                        name: actor.name,
                        email: actor.email,
                        role: actor.role,
                        isAdmin: actor.isAdmin,
                        userId: actor.userId,
                    })),
                discountCodes: discounts,
                stickers: stickers,
                assets: filterAssets(assets),
                products: getProducts(assets),
                inviteeName: fields.inviteeName,
                inviteeEmail: fields.inviteeEmail,
                sessionRequestId: fields.sessionRequestId,
                calendlyEventTypeUri: fields.calendlyEventTypeUri,
                calendlyEventUri: fields.calendlyEventUri,
                type: fields.type,
                isPublic: fields.isPublic,
                notifySubscribers: channelSubscriptionEnable && fields.notifySubscribers,
                hostProvider: fields.hostProvider,
                streamConnectors: [],
                mainImage: fields.mainImage,
                socialConnectors: connectors,
                labels: fields.labels,
                channelsIds: channelsIdsValue,
                ...(channelSubscriptionEnable && fields.notifySubscribers && enableTvChannelManager
                    ? { notifyChannelsIds: fields.channelsIds }
                    : {}),
                socialInfo: social,
                // record: fields.record,
                record: false,
                ...((fields.type === 'one_to_many' && { limitConcurrentUsers: fields.limitConcurrentUsers || 0 }) ||
                    {}),
                callToActions: (fields.callToActions || []).map((callToAction) => ({
                    ...callToAction,
                    callToActionType: callToAction.callToActionType || 'promotion',
                    id: generateRandomId(),
                    type: 'call-to-action',
                })),
                ...((fields.type === 'one_to_one' && { callOrigin: event.callOrigin }) || {}),
                duration: fields.duration || 30,
                skipLeadCreation: fields.skipLeadCreation,
            },
            onSuccess,
            onFailure,
        );
    }

    function getProducts(assets) {
        return assets
            .filter((asset) => asset.type === 'product' && (enableGift ? true : asset.price > 0))
            .map((a) => a.variantGroupId || a.id || a.externalId);
    }

    const visibleSteps = [
        t('event.eventFormNav.settings'),
        t('event.eventFormNav.actors'),
        t('event.eventFormNav.assets'),
        // isSocialTarget ? t('event.eventFormNav.social') : null,
        t('event.eventFormNav.confirm'),
    ];

    /**
     * This callback is called on success of creating/updating the event and
     * also on success of additional operation made after the event is saved.
     * @param {object} task Successfully executed task with structure...
     *  `{ task: 'event', 'data': undefined }`
     *  `{ task: 'event.connector', data: { provider: 'facebook', postId: '23432332323' } }`
     */
    const onSuccess = ({ task, data } = {}) => {
        restart();
    };

    /**
     * This callback is called on failure of creating/updating the event and
     * also on failure of additional tasks after the event is saved.
     * @param {object} task Failed task with structure...
     *  `{ task: 'event', 'data': undefined }`
     *  `{ task: 'event.connector', data: <error object> }`
     */
    const onFailure = (error) => {
        enqueueSnackbar(t('app.common.error'), { variant: 'error' });
    };

    const handleSetSteep = (s) => {
        closeSubForm();
        setStep(s);
    };

    return (
        <LayoutForm
            title={action}
            customClasses={flexBox.vH100}
            confirmDlg={{
                open: openCancelDlg,
                accept: finalCancel,
                cancel: cancelDlg,
                titleDlg: deleteConfirmTitle,
                messageDlg: deleteConfirmMessage,
            }}
        >
            <Stack direction="column" spacing={2} className={flexBox.overflowHidden}>
                {!subForm && !assetsPending && (
                    <Stack direction="column" spacing={3} className={cx(flexBox.overflowHidden, flexBox.vH100)}>
                        <FormNav step={step} setStep={handleSetSteep} steps={visibleSteps} wizardMode={wizardMode} />
                        <Stack direction="column" className={cx(flexBox.overflowHidden, flexBox.h100)}>
                            {
                                {
                                    1: (
                                        <EventFormSettings
                                            type={event.type}
                                            enableTvWidget={enableTvWidget}
                                            enableLabels={enableLabels}
                                            instant={event.instant}
                                            event={fields}
                                            handleFieldChange={handleFieldChange}
                                            step={step}
                                            setStep={handleSetSteep}
                                            cancel={cancel}
                                            open={openSubForm}
                                            close={closeSubForm}
                                            channelSubscriptionEnable={
                                                channelSubscriptionEnable &&
                                                (!event.isPublic || !event.id || event.isClone)
                                            }
                                            availableChannelsCount={availableChannels.length}
                                        />
                                    ),
                                    2: (
                                        <EventFormActors
                                            type={event.type}
                                            event={fields}
                                            enableDiscount={enableDiscount}
                                            handleFieldChange={handleFieldChange}
                                            step={step}
                                            setStep={handleSetSteep}
                                            guestLimit={guestLimit}
                                            cancel={cancel}
                                            actors={eventActors}
                                            setActors={setEventActors}
                                        />
                                    ),
                                    3: (
                                        <EventFormAssets
                                            assets={assets}
                                            enableGift={enableGift}
                                            enableDiscount={enableDiscount}
                                            step={step}
                                            setStep={handleSetSteep}
                                            open={openSubForm}
                                            close={closeSubForm}
                                            instantCallToActions={fields.callToActions}
                                            onConfirmInstantCallToActions={(instantCallToActions) => {
                                                handleFieldChange({
                                                    target: { value: instantCallToActions, name: 'callToActions' },
                                                });
                                            }}
                                            cancel={cancel}
                                        />
                                    ),
                                    4: (
                                        <EventFormConfirm
                                            type={event.type}
                                            event={fields}
                                            step={step}
                                            setStep={handleSetSteep}
                                            cancel={cancel}
                                            onSubmit={handleSubmit}
                                            actors={eventActors}
                                        />
                                    ),
                                }[step]
                            }
                        </Stack>
                    </Stack>
                )}
                {subForm}
            </Stack>
        </LayoutForm>
    );
};

export default EventForm;
